import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ClientInfoComponent } from '../client-info/client-info.component';
import { ClientSessionService, normalizeClientGuid, UiSession } from '@brighthub/uisession';
import { ClientInfoSkeletonComponent } from '../client-info-skeleton/client-info-skeleton.component';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import { CopilotFacadeService } from '@brighthub/copilot';
import { map } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientSearchService } from '../client-search.service';
import { GenericResponseCode } from '@brighthub/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Tooltip } from 'primeng/tooltip';

/** UI state for the Salesforce deep-link client lookup dialog. */
export type DeepLinkFetchDialogState = {
  visible: boolean;
  loading: boolean;
  errorMessage: string | null;
};

/**
 * Client conversation list for My Clients / FA app layouts. Handles Salesforce-style `?guid=` deep links
 * by selecting an existing active session or searching and opening a conversation for that client.
 */
@Component({
  selector: 'bn-client-list',
  standalone: true,
  imports: [
    CommonModule,
    ClientInfoComponent,
    ClientInfoSkeletonComponent,
    ScrollRegionComponent,
    Tooltip,
    Dialog,
    Button,
    ProgressSpinner,
  ],
  styleUrls: ['./client-list.component.scss'],
  templateUrl: './client-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientListComponent implements OnInit {
  @ViewChild(ScrollRegionComponent)
  private scrollRegion?: ScrollRegionComponent;

  readonly clientService = inject(ClientSessionService);
  readonly facade = inject(CopilotFacadeService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly clientSearch = inject(ClientSearchService);
  readonly showArchived = this.clientService.showArchived;

  /** Count of conversations in the active list with state `active` (excludes archived). */
  readonly activeConversationsTooltip = computed(() => {
    const n = this.clientService
      .sessions()
      .filter((s) => s.state === 'active').length;
    const label = n === 1 ? 'conversation' : 'conversations';
    return `${n} active ${label}`;
  });

  highlightedClientIds$ = this.facade.highlightedClientIds$;
  highlightedConversations$ = this.facade.highlightedConversations$;
  readonly selectedConversation = this.clientService.selectedConversationId;

  selectedConversation$ = this.facade.selectedConversation$;
  @Input() collapsed = false;

  /** Normalized `guid` from the route query string (Salesforce deep links). */
  private readonly _deepLinkGuid = toSignal(
    this.route.queryParamMap.pipe(
      map((params) => normalizeClientGuid(params.get('guid')))
    ),
    { initialValue: null as string | null }
  );

  /** Prevents duplicate in-flight searches for the same deep-link GUID. */
  private readonly _deepLinkSearchGuid = signal<string | null>(null);

  /** Last deep-link GUID fully handled (selection or error); cleared when `guid` leaves the URL. */
  private readonly _deepLinkProcessedGuid = signal<string | null>(null);

  /** Avoids duplicate copilot loads for the same UI session and thread. */
  private _lastActivatedSessionKey: string | null = null;

  /**
   * Modal shown while resolving a `?guid=` deep link via prototype search (no active session yet).
   * Non-closable while loading; shows an error and Close when lookup fails.
   */
  readonly deepLinkFetchDialog = signal<DeepLinkFetchDialogState>({
    visible: false,
    loading: false,
    errorMessage: null,
  });

  /** True when the deep-link fetch dialog may be dismissed (error state only). */
  readonly deepLinkFetchCanClose = computed(
    () =>
      this.deepLinkFetchDialog().visible && !this.deepLinkFetchDialog().loading
  );

  constructor() {
    effect(() => {
      const guid = this._deepLinkGuid();
      this.clientService.setPendingDeepLinkGuid(guid);
      if (!guid) {
        this._deepLinkSearchGuid.set(null);
        this._deepLinkProcessedGuid.set(null);
      }
    });

    effect(() => {
      const guid = this._deepLinkGuid();
      const ready = this.clientService.initialized();
      if (!guid || !ready) {
        return;
      }

      if (this._deepLinkProcessedGuid() === guid) {
        return;
      }

      const activeSession = this.clientService.findActiveSessionByGuid(guid);
      const selected = this.clientService.selectedSession();
      if (
        activeSession &&
        activeSession.uiSessionId === selected?.uiSessionId
      ) {
        this._markDeepLinkProcessed(guid);
        this._finishDeepLink();
        return;
      }

      if (activeSession) {
        this.clientService.createOrSelectClientConversation(activeSession);
        this.activateChat(activeSession);
        this._markDeepLinkProcessed(guid);
        this._finishDeepLink();
        return;
      }

      if (this._deepLinkSearchGuid() === guid) {
        const existingAfterSearch =
          this.clientService.findActiveSessionByGuid(guid);
        if (existingAfterSearch) {
          this.clientService.createOrSelectClientConversation(existingAfterSearch);
          this.activateChat(existingAfterSearch);
          this._markDeepLinkProcessed(guid);
          this._finishDeepLink();
        }
        return;
      }
      this._deepLinkSearchGuid.set(guid);
      this._openDeepLinkFetchDialog();

      this.clientSearch.searchClients(guid).subscribe({
        next: (res) => {
          this._deepLinkSearchGuid.set(null);
          const first = res.payload?.clients?.[0];
          const afterLoad = this.clientService.findActiveSessionByGuid(guid);
          if (afterLoad) {
            this.clientService.createOrSelectClientConversation(afterLoad);
            this.activateChat(afterLoad);
            this._markDeepLinkProcessed(guid);
            this._finishDeepLink();
            return;
          }
          if (
            res.result.code === GenericResponseCode.SERVICE_OK &&
            first?.guid
          ) {
            this.clientService.createOrSelectClientConversation(first.guid);
            this._markDeepLinkProcessed(guid);
            this._finishDeepLink();
            this._closeDeepLinkFetchDialog();
          } else {
            this._markDeepLinkProcessed(guid);
            this._showDeepLinkFetchError(`No client found for ID ${guid}`);
          }
        },
        error: (err) => {
          this._deepLinkSearchGuid.set(null);
          this._markDeepLinkProcessed(guid);
          const msg = `Error searching for client: ${err.message || 'Unknown error'}`;
          this._showDeepLinkFetchError(msg);
        },
      });
    });

    effect(() => {
      const selected = this.clientService.selectedSession();
      if (selected) {
        this.activateChat(selected);
      }
    });

    effect(() => {
      this.facade.disableInput.set(this.clientService.showArchived());
    });
  }

  ngOnInit() {
    this.clientService.loadClientSessions();
  }

  private _markDeepLinkProcessed(guid: string): void {
    this._deepLinkProcessedGuid.set(guid);
  }

  /** Clears pending deep-link state and removes `guid` from the URL after handling. */
  private _finishDeepLink(): void {
    this._deepLinkSearchGuid.set(null);
    this.clientService.setPendingDeepLinkGuid(null);
    queueMicrotask(() => {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { guid: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      }).then(() => {
        if (!this._deepLinkGuid()) {
          this._deepLinkProcessedGuid.set(null);
        }
      });
    });
  }

  private _openDeepLinkFetchDialog(): void {
    this.deepLinkFetchDialog.set({
      visible: true,
      loading: true,
      errorMessage: null,
    });
  }

  private _showDeepLinkFetchError(message: string): void {
    this.deepLinkFetchDialog.set({
      visible: true,
      loading: false,
      errorMessage: message,
    });
  }

  private _closeDeepLinkFetchDialog(): void {
    this.deepLinkFetchDialog.set({
      visible: false,
      loading: false,
      errorMessage: null,
    });
  }

  /**
   * Dismisses the deep-link fetch dialog after an error; clears the `guid` query param and
   * selects the most recent active conversation.
   */
  closeDeepLinkFetchDialog(): void {
    const hadError = this.deepLinkFetchDialog().errorMessage !== null;
    this._closeDeepLinkFetchDialog();
    if (hadError) {
      this._finishDeepLink();
      this._selectMostRecentActiveSession();
    }
  }

  /** Selects the newest active UI session (same ordering as {@link ClientSessionService.sessions}). */
  private _selectMostRecentActiveSession(): void {
    const latest = this.clientService.sessions()[0];
    if (!latest) {
      return;
    }
    this._lastActivatedSessionKey = null;
    this.clientService.createOrSelectClientConversation(latest);
    this.activateChat(latest);
  }

  /**
   * @param visible PrimeNG dialog visibility; ignored while loading (non-closable).
   */
  onDeepLinkDialogVisibleChange(visible: boolean): void {
    if (visible) {
      return;
    }
    if (this.deepLinkFetchCanClose()) {
      this.closeDeepLinkFetchDialog();
    } else {
      this.deepLinkFetchDialog.update((state) => ({ ...state, visible: true }));
    }
  }

  /**
   * User clicked a client row: session service is updated in {@link ClientInfoComponent.selectClient};
   * this loads the copilot thread (including when re-clicking the already-selected row).
   */
  onSessionClick(uiSession: UiSession) {
    this._lastActivatedSessionKey = null;
    this.activateChat(uiSession);
  }

  /**
   * Loads the copilot conversation for a UI session and updates list selection state.
   *
   * @remarks Skips duplicate facade loads for the same subject/conversation pair; use
   * {@link onSessionClick} when the user explicitly selects a row (clears that guard).
   * No-ops when {@link UiSession.conversationId} is absent so async session creation can
   * finish and the {@link ClientSessionService.selectedSession} effect can activate with a real id.
   */
  activateChat(uiSession: UiSession) {
    if (this.deepLinkFetchDialog().visible && this.deepLinkFetchDialog().loading) {
      this._closeDeepLinkFetchDialog();
    }

    if (!uiSession.conversationId) {
      return;
    }

    const subjectId = uiSession.subjectId;
    const conversationId = uiSession.conversationId;
    const sessionKey = `${uiSession.uiSessionId}:${subjectId}:${conversationId}`;

    if (this._lastActivatedSessionKey === sessionKey) {
      return;
    }
    this._lastActivatedSessionKey = sessionKey;

    this.clientService.setSelectedConversation(conversationId);
    this.facade.selectClient(subjectId);
    this.facade.loadAndSelectConversation(
      subjectId,
      conversationId,
      uiSession.state,
      true,
      uiSession.subjectData.guid
    );
    this.scrollToSelected(conversationId);
    this.clientService.setSelectedSubject(uiSession.subjectData);
    this.clientService.rememberLastSession(uiSession);
  }

  scrollToSelected(conversationId: string) {
    queueMicrotask(() => {
      const container = this.scrollRegion?.viewportElement;
      const target = container?.querySelector(
        `[data-conversation="${conversationId}"]`
      );

      if (container instanceof HTMLElement && target instanceof HTMLElement) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        const offsetTop =
          targetRect.top - containerRect.top + container.scrollTop;

        container.scrollTo({
          top: offsetTop - 12,
          behavior: 'smooth',
        });

        target.focus?.();
      }
    });
  }

  viewArchived() {
    this.clientService.toggleArchivedView();
    const view = this.showArchived() ? 'archived' : 'active';
    this.clientService.restorePreviousSessionOrClear(view);
  }
}
